/**
 * Regras da tela de importação do Acessórias (04/11/2026): agrupar o que fechou por
 * empresa, buscar, filtrar o que ficou de fora e formatar números para a diretoria.
 */
import type {
  IConferenciaDoMes,
  IFechadaDaImportacao,
  IIgnoradaDoAcessorias,
  IRegistroDaImportacao,
  ITotaisDaImportacao,
} from '../../../../hooks/useFechamentoContabilMensal';

/** tipo do motivo -> como a tela chama (os tipos vêm da API) */
export const MOTIVOS: Record<string, string> = {
  sem_cadastro: 'CNPJ fora do cadastro',
  nao_cliente: 'Não é cliente no mês',
  desobrigado: 'Contábil “Desobrigado”',
  sem_cnpj: 'CPF ou documento inválido',
  filial: 'Filial (o fechamento é da matriz)',
  fora_dominio: 'Fora do Domínio',
  sem_balancete: 'Sem balancete no Domínio',
  reaberta_pela_area: 'Reaberta pela área no sistema',
  mudou: 'Mudou durante a importação',
};

export const ACOES_DO_SISTEMA: Record<string, string> = {
  fechar: 'Fechada sem movimento',
  dominio: 'Fechada (encerrada no Domínio)',
  reabrir: 'Reaberta (fora de ordem)',
};

const so = (n: number, casas = 1) =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

/** "30,7%"; "—" sem base. */
export const pct = (valor: number | null | undefined): string =>
  valor === null || valor === undefined ? '—' : `${so(valor)}%`;

/** Variação em pontos percentuais: "+27,4 p.p.", "0,0 p.p.", "—". */
export const variacao = (
  antes: number | null | undefined,
  depois: number | null | undefined,
): string => {
  if (antes === null || antes === undefined || depois === null || depois === undefined) return '—';
  const v = Math.round((depois - antes) * 10) / 10;
  return `${v > 0 ? '+' : ''}${so(v)} p.p.`;
};

/** "02/2026" */
export const mesAno = (competencia: string): string =>
  /^\d{4}-\d{2}$/.test(competencia ?? '')
    ? `${competencia.slice(5, 7)}/${competencia.slice(0, 4)}`
    : (competencia ?? '');

/** "15/03/2026" a partir de "2026-03-15". */
export const dataCurta = (iso: string | null | undefined): string => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso ?? '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
};

/** "11.111.111/0001-11"; o que não tem 14 dígitos volta como veio. */
export const cnpjFormatado = (doc: string | null | undefined): string => {
  const d = (doc ?? '').replace(/\D/g, '');
  return d.length === 14
    ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
    : (doc ?? '');
};

/** "812 KB", "1,4 MB". */
export const tamanho = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${so(bytes / (1024 * 1024))} MB`;
};

/** Os totais do registro: na lista vêm em `totais`; no detalhe, no resumo. */
export const totaisDo = (r: IRegistroDaImportacao): ITotaisDaImportacao => {
  const t = r.resumo?.totais ?? r.totais ?? r.resumo;
  return {
    linhas: t?.linhas ?? 0,
    consideradas: t?.consideradas ?? 0,
    fechadas: t?.fechadas ?? 0,
    fechadas_pelo_sistema: t?.fechadas_pelo_sistema ?? 0,
    ja_fechadas: t?.ja_fechadas ?? 0,
    ignoradas: t?.ignoradas ?? 0,
    duplicadas: t?.duplicadas ?? r.resumo?.duplicadas ?? 0,
  };
};

export interface IEmpresaFechada {
  codigo: number;
  razao: string;
  cnpj: string;
  competencias: string[];
  comPendencia: number;
  travadas: number;
  responsaveis: string[];
}

/** O que fechou, uma linha por empresa, em ordem de razão social. */
export const porEmpresa = (fechadas: IFechadaDaImportacao[]): IEmpresaFechada[] => {
  const grupos = new Map<number, IEmpresaFechada>();
  fechadas.forEach(f => {
    const g = grupos.get(f.codigo) ?? {
      codigo: f.codigo,
      razao: f.razao ?? '',
      cnpj: f.cnpj,
      competencias: [],
      comPendencia: 0,
      travadas: 0,
      responsaveis: [],
    };
    g.competencias.push(f.competencia);
    if (f.com_pendencia) g.comPendencia += 1;
    if (f.situacao_anterior === 'Travada') g.travadas += 1;
    if (f.responsavel && !g.responsaveis.includes(f.responsavel))
      g.responsaveis.push(f.responsavel);
    grupos.set(f.codigo, g);
  });
  return Array.from(grupos.values())
    .map(g => ({ ...g, competencias: [...g.competencias].sort() }))
    .sort((a, b) => a.razao.localeCompare(b.razao, 'pt-BR') || a.codigo - b.codigo);
};

const normal = (t: string) => (t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Busca por razão social, código ou CNPJ (com ou sem pontuação). */
export const confere = (
  item: { codigo: number | string | null; razao?: string | null; cnpj?: string | null },
  busca: string,
): boolean => {
  const termo = normal(busca.trim());
  if (!termo) return true;
  const digitos = termo.replace(/\D/g, '');
  return (
    normal(item.razao ?? '').includes(termo) ||
    String(item.codigo ?? '') === termo ||
    (digitos.length >= 3 && (item.cnpj ?? '').replace(/\D/g, '').includes(digitos))
  );
};

/** Os motivos de fora, do mais frequente ao menos. */
export const motivosDeFora = (ignoradas: IIgnoradaDoAcessorias[]): [string, number][] => {
  const conta: Record<string, number> = {};
  ignoradas.forEach(i => {
    conta[i.motivo_tipo] = (conta[i.motivo_tipo] ?? 0) + 1;
  });
  return Object.entries(conta).sort((a, b) => b[1] - a[1]);
};

/** As colunas da conferência por mês: cada linha da planilha cai em uma só. */
export const COLUNAS_DA_CONFERENCIA: {
  chave: keyof Omit<
    IConferenciaDoMes,
    'linhas' | 'ignoradas_por_motivo' | 'fechadas_pelo_sistema_em_seguida'
  >;
  rotulo: string;
  dica: string;
}[] = [
  {
    chave: 'fechadas_agora',
    rotulo: 'Fechadas agora',
    dica: 'A importação fechou a competência como entregue pela área',
  },
  {
    chave: 'ja_fechadas_area',
    rotulo: 'Já fechadas · área (Domínio)',
    dica: 'Já estava fechada antes: a área encerrou a competência no Domínio',
  },
  {
    chave: 'ja_fechadas_sistema',
    rotulo: 'Já fechadas · sistema',
    dica: 'Já estava fechada antes: o sistema fechou por não ter movimento',
  },
  {
    chave: 'ja_fechadas_outras',
    rotulo: 'Já fechadas · outras',
    dica: 'Já estava fechada antes: pela tela ou por outra importação',
  },
  {
    chave: 'ignoradas',
    rotulo: 'Ficaram de fora',
    dica: 'Não fecha no sistema: não é cliente no mês, filial, sem balancete…',
  },
  {
    chave: 'duplicadas',
    rotulo: 'Linhas repetidas',
    dica: 'Mesma empresa e competência de outra linha da planilha: conta uma vez',
  },
  {
    chave: 'nao_consideradas',
    rotulo: 'Não é fechamento contábil',
    dica: 'Linha de outra obrigação ou sem data de entrega',
  },
];

/** A soma das colunas de todos os meses (a linha "Ano" da conferência). */
export const totalDaConferencia = (
  porMes: Record<string, IConferenciaDoMes>,
): IConferenciaDoMes => {
  const total: IConferenciaDoMes = {
    linhas: 0,
    fechadas_agora: 0,
    ja_fechadas_area: 0,
    ja_fechadas_sistema: 0,
    ja_fechadas_outras: 0,
    ignoradas: 0,
    ignoradas_por_motivo: {},
    duplicadas: 0,
    nao_consideradas: 0,
    fechadas_pelo_sistema_em_seguida: 0,
  };
  Object.values(porMes).forEach(m => {
    total.linhas += m.linhas;
    total.fechadas_pelo_sistema_em_seguida += m.fechadas_pelo_sistema_em_seguida;
    COLUNAS_DA_CONFERENCIA.forEach(({ chave }) => {
      total[chave] += m[chave];
    });
    Object.entries(m.ignoradas_por_motivo).forEach(([tipo, qtd]) => {
      total.ignoradas_por_motivo[tipo] = (total.ignoradas_por_motivo[tipo] ?? 0) + qtd;
    });
  });
  return total;
};

/** A soma das colunas bate com as linhas da planilha? */
export const somaConfere = (m: IConferenciaDoMes): boolean =>
  COLUNAS_DA_CONFERENCIA.reduce((s, { chave }) => s + m[chave], 0) === m.linhas;

/** Só os itens da competência escolhida ("" = todos). */
export const daCompetencia = <T extends { competencia: string }>(itens: T[], mes: string): T[] =>
  mes ? itens.filter(i => i.competencia === mes) : itens;
